import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import DOMPurify from 'dompurify';

const Section = ({
  type,
  label,
  items,
  addingType,
  setAddingType,
  newAccountName,
  setNewAccountName,
  newAccountCategory,
  setNewAccountCategory,
  addAccount,
  removeAccount,
  updateAccountCategory
}) => {
  const [editingCategoryFor, setEditingCategoryFor] = useState(null);
  const [categoryDraft, setCategoryDraft] = useState('');

  const commitCategory = (account) => {
    updateAccountCategory(account.name, type, categoryDraft);
    setEditingCategoryFor(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3.5">
        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
        <button
          onClick={() => setAddingType(type)}
          className="flex items-center gap-1.5 text-xs text-accent hover:text-emerald-700 font-medium transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>

      {addingType === type && (
        <div className="flex flex-col gap-2 mb-3 bg-gray-50 border border-gray-200 rounded-xl p-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={newAccountName}
              onChange={(e) => setNewAccountName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addAccount(type)}
              placeholder="Account name…"
              maxLength={50}
              autoFocus
              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100 placeholder-gray-400"
            />
            <button
              onClick={() => addAccount(type)}
              className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => { setAddingType(null); setNewAccountName(''); setNewAccountCategory(''); }}
              className="px-3 py-2 text-gray-500 hover:text-gray-900 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
          <input
            type="text"
            value={newAccountCategory}
            onChange={(e) => setNewAccountCategory(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addAccount(type)}
            placeholder="Category (optional), e.g. Retirement"
            maxLength={30}
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-600 text-xs outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100 placeholder-gray-400"
          />
        </div>
      )}

      <div className="border border-gray-100 rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            No {label.toLowerCase()} yet.
          </div>
        ) : (
          items.map((account, i) => (
            <div
              key={account.id}
              className={`flex items-center justify-between px-4 py-3 group hover:bg-gray-50 transition-colors ${
                i < items.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-gray-900 truncate">{DOMPurify.sanitize(account.name)}</span>
                {editingCategoryFor === account.id ? (
                  <input
                    type="text"
                    autoFocus
                    value={categoryDraft}
                    maxLength={30}
                    onChange={(e) => setCategoryDraft(e.target.value)}
                    onBlur={() => commitCategory(account)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitCategory(account);
                      if (e.key === 'Escape') setEditingCategoryFor(null);
                    }}
                    placeholder="Category…"
                    className="mt-1 px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600 text-xs outline-none focus:border-accent w-32"
                  />
                ) : (
                  <button
                    onClick={() => { setEditingCategoryFor(account.id); setCategoryDraft(account.category || ''); }}
                    className="mt-0.5 text-left text-xs text-gray-400 hover:text-accent transition-colors truncate"
                  >
                    {account.category ? DOMPurify.sanitize(account.category) : '+ category'}
                  </button>
                )}
              </div>
              <button
                onClick={() => removeAccount(account.name, type === 'asset' ? 'asset' : 'debt')}
                className="text-gray-300 hover:text-negative opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const AccountManager = ({
  accounts,
  setShowAccountManager,
  addingType,
  setAddingType,
  newAccountName,
  setNewAccountName,
  newAccountCategory,
  setNewAccountCategory,
  addAccount,
  removeAccount,
  updateAccountCategory
}) => (
  <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white border border-gray-100 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
        <h2 className="text-gray-900 font-semibold text-lg tracking-tight">Manage Accounts</h2>
        <button onClick={() => setShowAccountManager(false)} className="text-gray-400 hover:text-gray-900 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-7 space-y-8">
        <Section
          type="asset"
          label="Assets"
          items={accounts.assets}
          addingType={addingType}
          setAddingType={setAddingType}
          newAccountName={newAccountName}
          setNewAccountName={setNewAccountName}
          newAccountCategory={newAccountCategory}
          setNewAccountCategory={setNewAccountCategory}
          addAccount={addAccount}
          removeAccount={removeAccount}
          updateAccountCategory={updateAccountCategory}
        />
        <Section
          type="debt"
          label="Debts"
          items={accounts.debts}
          addingType={addingType}
          setAddingType={setAddingType}
          newAccountName={newAccountName}
          setNewAccountName={setNewAccountName}
          newAccountCategory={newAccountCategory}
          setNewAccountCategory={setNewAccountCategory}
          addAccount={addAccount}
          removeAccount={removeAccount}
          updateAccountCategory={updateAccountCategory}
        />
      </div>
    </div>
  </div>
);

export default AccountManager;
